import {
  Table,
  Tag,
  Popconfirm,
  Pagination,
  Tooltip,
  Switch,
  Spin,
  Modal,
} from "antd";
import S3Image from "../../../component/S3Image";
import type { ColumnsType } from "antd/es/table";
import { IoTrashOutline } from "react-icons/io5";
import { BiEdit } from "react-icons/bi";
import { PlayCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { useState } from "react";
import "../styles.css";

function DataTable(props: any) {
  const [loadingRows, setLoadingRows] = useState<{ [key: number]: boolean }>(
    {}
  );

  // Video preview functions
  const getThumbnailUrl = (youtubeId: string): string => {
    return `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;
  };

  const getEmbedUrl = (youtubeId: string): string => {
    return `https://www.youtube.com/embed/${youtubeId}`;
  };

  const handleVideoPreview = (record: any) => {
    const embedUrl = getEmbedUrl(record.video_id);
    if (embedUrl) {
      Modal.info({
        title: record.description || "Video Preview",
        content: (
          <iframe
            width="100%"
            height="315"
            src={embedUrl}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ),
        width: 600,
        icon: null,
      });
    }
  };

  interface DataType {
    key: string;
    id: number;
    video_id: string;
    description: string;
    createdAt: string;
    updatedAt: string;
    is_published: boolean;
    user: any;
    is_deleted: boolean;
  }

  const columns: ColumnsType<DataType> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 50,
      align: "left",
      render: (text, record, index) => {
        return <div style={{ textAlign: "left" }}>{index + 1}</div>;
      },
    },
    {
      title: "Video",
      dataIndex: "video_id",
      key: "video_thumbnail",
      width: 120,
      align: "center",
      render: (text, record: any) => {
        if (!record.video_id) {
          return (
            <div style={{ textAlign: "center", color: "#999" }}>No Video</div>
          );
        }

        const thumbnailUrl = getThumbnailUrl(record.video_id);
        return (
          <div style={{ position: "relative", display: "inline-block" }}>
            <S3Image
              src={thumbnailUrl}
              alt={record.description || "Video thumbnail"}
              width={80}
              height={60}
              style={{
                objectFit: "cover",
                borderRadius: 4,
                cursor: "pointer",
              }}
              preview={false}
              onClick={() => handleVideoPreview(record)}
            />
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: 20,
                color: "white",
                textShadow: "0 2px 4px rgba(0,0,0,0.8)",
                cursor: "pointer",
              }}
              onClick={() => handleVideoPreview(record)}
            >
              <PlayCircleOutlined />
            </div>
            <div
              style={{
                fontSize: "10px",
                color: "#666",
                textAlign: "center",
                marginTop: 4,
                maxWidth: 80,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {record.video_id}
            </div>
          </div>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      align: "center",
      render: (text, record: any) => {
        const description = record.description || "";
        const truncated =
          description.length > 100
            ? description.substring(0, 100) + "..."
            : description;
        return (
          <div style={{ fontSize: 12, color: "#666", textAlign: "center" }}>
            {truncated || "N/A"}
          </div>
        );
      },
    },
    {
      title: "Created By",
      dataIndex: "user",
      key: "user",
      width: 150,
      align: "center",
      render: (user: any) => {
        return (
          <div style={{ fontSize: "12px", textAlign: "center" }}>
            {user?.username || user?.attributes?.username || "N/A"}
          </div>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      align: "center",
      render: (text, record: any) => {
        return (
          <div style={{ fontSize: 12, textAlign: "center" }}>
            {record?.createdAt
              ? moment(record.createdAt).format("DD-MM-YYYY h:mm A")
              : "N/A"}
          </div>
        );
      },
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      align: "center",
      render: (text, record: any) => {
        return (
          <div style={{ fontSize: 12, textAlign: "center" }}>
            {record?.updatedAt
              ? moment(record.updatedAt).format("DD-MM-YYYY h:mm A")
              : "N/A"}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      align: "center",
      render: (text, record: any) => {
        const isPublished = record?.is_published === true;
        const isLoading = loadingRows[record.id] || false;

        if (record?.is_deleted === true) {
          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Tag color="red" style={{ fontSize: "10px", margin: 0 }}>
                Deleted
              </Tag>
            </div>
          );
        }

        return (
          <div
            className="highlights-toggle-switch"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Tooltip
              title={isPublished ? "Click to unpublish" : "Click to publish"}
            >
              <Switch
                checked={isPublished}
                loading={isLoading}
                disabled={isLoading}
                size="default"
                checkedChildren={
                  <span
                    style={{
                      fontSize: "10px",
                      transition: "opacity 200ms ease-in-out",
                    }}
                  >
                    Published
                  </span>
                }
                unCheckedChildren={
                  <span
                    style={{
                      fontSize: "10px",
                      transition: "opacity 200ms ease-in-out",
                    }}
                  >
                    Draft
                  </span>
                }
                style={{
                  minWidth: "60px",
                }}
                className="highlights-smooth-switch"
                onChange={(checked) => {
                  if (props.onTogglePublish) {
                    setLoadingRows((prev) => ({ ...prev, [record.id]: true }));
                    props.onTogglePublish(record, checked, () => {
                      setLoadingRows((prev) => {
                        const newState = { ...prev };
                        delete newState[record.id];
                        return newState;
                      });
                    });
                  }
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      align: "center",
      render: (_, record: any) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Tooltip title="Edit">
              <BiEdit
                color="#0055a6"
                size={18}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (props.onEdit) {
                    props.onEdit(record);
                  }
                }}
              />
            </Tooltip>
            <Popconfirm
              title="Delete item"
              description="Are you sure to delete this item?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => {
                if (props.onDelete) {
                  props.onDelete(record);
                }
              }}
            >
              <Tooltip title="Delete">
                <IoTrashOutline
                  color="red"
                  size={18}
                  style={{ cursor: "pointer" }}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ marginTop: 10 }}>
      <style>
        {`
          .highlights-toggle-switch .ant-switch-handle,
          .highlights-smooth-switch .ant-switch-handle {
            transition: left 300ms cubic-bezier(0.4, 0, 0.2, 1) !important;
            will-change: left !important;
          }
          .highlights-toggle-switch .ant-switch-checked .ant-switch-handle,
          .highlights-smooth-switch.ant-switch-checked .ant-switch-handle {
            transition: left 300ms cubic-bezier(0.4, 0, 0.2, 1) !important;
            will-change: left !important;
          }
        `}
      </style>
      <Table
        columns={columns}
        dataSource={props.data}
        className="commen-table-custom-scrollbar"
        pagination={false}
        size="small"
        rowKey={(record: any) => String(record?.id || Math.random())}
      />
      <br />
      <div
        style={{
          float: "right",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {props.loading ? (
          <Spin size="small" />
        ) : (
          <>
            <Pagination
              current={props?.page || 1}
              total={props?.meta || 0}
              pageSize={props?.pagesize || 10}
              onChange={(page, pageSize) => {
                if (props.onPageChange) {
                  props.onPageChange(page, pageSize);
                }
              }}
              onShowSizeChange={(current, size) => {
                if (props.onPageChange) {
                  props.onPageChange(current, size);
                }
              }}
              showSizeChanger={true}
              showQuickJumper={false}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} items`
              }
              pageSizeOptions={["10", "25", "50", "100"]}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default DataTable;
